import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
} from '@mui/material';
import { Add, Search, LocalOffer } from '@mui/icons-material';
import API from '../../services/api';
import CustomSnackBar from '../../components/CustomSnackBar';

export default function AddBonusToInvoice({ invoiceId, orderIds, supplierId, companyId, open, onAdded, setOpen }) {
  const [loading, setLoading] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({}); // bonus_id -> { quantity, prformat, comment }
  const [formats, setFormats] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!open) return;
      setLoadingRows(true);
      try {
        let items = [];
        if (Array.isArray(orderIds) && orderIds.length > 0) {
          const res = await API.get('order-Av/unified_products/', {
            params: { order_ids: orderIds.join(',') }
          });
          items = (res.data?.order_bonus || []).map(b => ({
            id: b.id,
            order_bonus_id: b.id,
            product: b.product,
            prformat: b.prformat,
            quantity: b.quantity ?? 0,
            taken_quantity: b.taken_quantity ?? 0,
            remaining_quantity: b.remaining_quantity ?? 0,
          }));
        }

        if (items.length === 0 && supplierId) {
          const payload = { supplier_id: Number(supplierId) };
          if (companyId) payload.company_id = Number(companyId);
          const productsRes = await API.post('product/getProductsBySupplier/', payload);
          items = (Array.isArray(productsRes.data) ? productsRes.data : [])
            .filter((p) => p?.is_active !== false)
            .map((p) => ({
              id: `manual-${p.id}`,
              order_bonus_id: null,
              product: p,
              prformat: p?.prformat || null,
              quantity: 0,
              taken_quantity: 0,
              remaining_quantity: Number(p?.units) > 0 ? Number(p.units) : 999999,
            }));
        }

        setRows(items);
        try {
          const { data } = await API.get('invoice/catalogs/create_invoice/');
          setFormats(Array.isArray(data?.formats) ? data.formats : []);
        } catch { setFormats([]); }
      } catch (e) {
        console.error(e);
        setSnack({ open: true, message: 'Error cargando bonificaciones', severity: 'error' });
        setRows([]);
      } finally {
        setLoadingRows(false);
      }
    };
    load();
  }, [open, orderIds, supplierId, companyId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      String(r.product?.code || '').toLowerCase().includes(q) ||
      String(r.product?.description || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const updateField = (bid, field, value) => setForm(prev => ({ ...prev, [bid]: { ...(prev[bid] || {}), [field]: value } }));

  const handleAdd = async (row) => {
    const bid = row.order_bonus_id ?? row.id;
    const pid = row.product?.id;
    const cfg = form[bid] || {};
    const qty = Number(cfg.quantity || 0);
    if (!qty || qty <= 0) {
      setSnack({ open: true, message: 'Ingrese cantidad válida', severity: 'warning' });
      return;
    }
    if (qty > Number(row.remaining_quantity || 0)) {
      setSnack({ open: true, message: 'Cantidad excede la disponibilidad', severity: 'warning' });
      return;
    }
    try {
      setLoading(true);
      await API.post('invoice-bonus/', {
        invoice_id: invoiceId,
        product_id: pid,
        order_bonus_id: row.order_bonus_id ?? null,
        quantity: qty,
        quantityRe: qty,
        prformat_id: cfg.prformat_id ?? cfg.prformat?.id ?? row.prformat?.id ?? null,
        comment: cfg.comment ?? '',
      });
      setSnack({ open: true, message: 'Bonificación agregada', severity: 'success' });
      if (onAdded) await onAdded();
      setOpen(false);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || 'No se pudo agregar';
      setSnack({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        size='small'
        placeholder='Buscar por código o descripción'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: (<InputAdornment position='start'><Search /></InputAdornment>) }}
      />
      <TableContainer component={Paper} variant='outlined' sx={{ maxHeight: 420 }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 140 }}>Código</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align='right' style={{ width: 120 }}>Disp.</TableCell>
              <TableCell align='right' style={{ width: 110 }}>Cantidad</TableCell>
              <TableCell style={{ width: 140 }}>Formato</TableCell>
              <TableCell style={{ width: 200 }}>Comentario</TableCell>
              <TableCell align='center' style={{ width: 80 }}>Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingRows && (
              <TableRow><TableCell colSpan={7} align='center'>Cargando...</TableCell></TableRow>
            )}
            {!loadingRows && filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} align='center'>Sin resultados</TableCell></TableRow>
            )}
            {filtered.map((r) => {
              const bid = r.order_bonus_id ?? r.id;
              const cfg = form[bid] || {};
              return (
                <TableRow key={bid} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalOffer fontSize='small' color='success' />
                      {r.product?.code}
                    </Box>
                  </TableCell>
                  <TableCell>{r.product?.description}</TableCell>
                  <TableCell align='right'>{Number(r.remaining_quantity || 0).toLocaleString()}</TableCell>
                  <TableCell align='right'>
                    <TextField size='small' type='number' sx={{ width: 100 }} value={cfg.quantity || ''}
                      onChange={(e) => updateField(bid, 'quantity', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size='small'
                      sx={{ width: 160 }}
                      value={cfg.prformat_id ?? r.prformat?.id ?? ''}
                      onChange={(e) => {
                        const id = e.target.value || null;
                        const selected = formats.find(f => String(f.id) === String(id)) || null;
                        setForm(prev => ({ ...prev, [bid]: { ...(prev[bid] || {}), prformat_id: id, prformat: selected } }));
                      }}
                    >
                      <MenuItem value=''>—</MenuItem>
                      {formats.map(f => (
                        <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField size='small' sx={{ width: 180 }} value={cfg.comment ?? ''}
                      onChange={(e) => updateField(bid, 'comment', e.target.value)} />
                  </TableCell>
                  <TableCell align='center'>
                    <Tooltip title='Agregar'>
                      <span>
                        <IconButton color='primary' onClick={() => handleAdd(r)} disabled={loading}>
                          <Add />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomSnackBar
        open={snack.open}
        setOpen={(v) => setSnack(prev => ({ ...prev, open: v }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}

